import scrollIntoView from 'scroll-into-view-if-needed';

import { isStoryboardGenerationInProgress } from 'js/src/data/app';
import {
    clearFocusedAsset,
    setFocusedAssetInfo,
    setFocusedSceneInfo,
} from 'js/src/data/scenes/scenes-actions';
import { getFocusedSceneId, getFocusedSceneInfo } from 'js/src/data/scenes/scenes-selectors';
import {
    getEditorFromSceneGroupId,
    SCROLL_TRANSCRIPT_COMMAND,
    TRANSCRIPT_SCROLL_OPTIONS,
} from 'js/src/data/transcript';
import FocusAndScroll from 'js/src/libs/focus-and-scroll';
import type { AppDispatch } from 'js/src/libs/RootState';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

/**
 * There are a variety of changes that can happen which remove the currently editing asset.
 * For example, swapping a scene design, deleting a scene, undo/redo, etc. This operation
 * is a convenience function to check if the currently editing asset is still valid, and
 * if not, clear it. That way we don't have to check for this in all the places where
 * we might remove the currently editing asset.
 */
export const clearEditingAssetIfRemovedOperation = createLuminaryOperation(
    ({ luminary, dispatch, getState }) => {
        const data = getFocusedSceneInfo(getState());
        if (!data) {
            return;
        }

        const sceneId = data.get('sceneId');
        const scene = sceneId ? luminary.getSceneById(sceneId) : undefined;
        if (!scene) {
            dispatch(clearFocusedAsset());
            return;
        }

        const subsceneIndex = data.get('subsceneIndex');
        const subscene = subsceneIndex !== null ? scene.subscenes[subsceneIndex] : undefined;
        if (!subscene) {
            dispatch(clearFocusedAsset());
            return;
        }

        const assetId = data.get('assetId');
        const asset = assetId ? scene.getAssetById(assetId) : undefined;
        if (!asset) {
            dispatch(clearFocusedAsset());
        }
    },
);

export function setFocusedSceneAndScroll(sceneId: string, sceneGroupId: string, subsceneIndex = 0) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        // Should not scroll if storyboard generation is in progress
        if (isStoryboardGenerationInProgress(getState())) {
            return;
        }
        dispatch(setFocusedSceneInfo({ sceneId, sceneGroupId, subsceneIndex }));
        FocusAndScroll.scrollToSlide(sceneId, subsceneIndex);
    };
}

export function setFocusedAssetAndScroll({
    sceneId,
    sceneGroupId,
    subsceneIndex,
    assetId,
    assetType,
    rootAssetGroupId,
}: {
    sceneId: string;
    sceneGroupId: string;
    subsceneIndex: number;
    assetId: string;
    assetType: string;
    rootAssetGroupId?: string | null;
}) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        if (isStoryboardGenerationInProgress(getState())) {
            return;
        }
        dispatch(
            setFocusedAssetInfo({
                sceneId,
                sceneGroupId,
                subsceneIndex,
                assetId,
                assetType,
                rootAssetGroupId,
            }),
        );
        FocusAndScroll.scrollToSlide(sceneId, subsceneIndex);
    };
}

export function _getNonVoicedriveScrollTarget(sceneId: string) {
    return `.transcript-scene-preview[data-sceneid="${sceneId}"]`;
}

/**
 * Rules for determining the active scene:
 * 1. After loading the creator, the first scene is always an `active scene'.
 * 2. On scrolling
 *    if the last active scene is out of the view, we find the scene closest to the center and make it `active scene`
 */
export function updateFocusedSceneInfoOnScroll() {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const currFocusedSceneInfo = getFocusedSceneInfo(getState());
        const centermostSceneInfo = FocusAndScroll.calculateCentermostSceneInfo();
        if (centermostSceneInfo && !isStoryboardGenerationInProgress(getState())) {
            const { center_slide_id, center_sub_scene_index, center_slide_group_id } =
                centermostSceneInfo;
            const hasSceneGroupChanged =
                currFocusedSceneInfo.get('sceneGroupId') !== center_slide_group_id;
            const hasSceneChanged = currFocusedSceneInfo.get('sceneId') !== center_slide_id;
            const hasSubsceneIndexChanged =
                currFocusedSceneInfo.get('subsceneIndex') !== center_sub_scene_index;
            const hasAnythingChanged =
                hasSceneGroupChanged || hasSceneChanged || hasSubsceneIndexChanged;
            if (hasAnythingChanged) {
                dispatch(
                    setFocusedSceneInfo({
                        sceneId: center_slide_id,
                        sceneGroupId: center_slide_group_id,
                        subsceneIndex: center_sub_scene_index,
                    }),
                );
                const editor = getEditorFromSceneGroupId(getState(), center_slide_group_id);
                if (editor) {
                    editor.dispatchCommand(SCROLL_TRANSCRIPT_COMMAND, {
                        sceneId: center_slide_id,
                    });
                } else {
                    const nonVoicedrivenScenePreview = document.querySelector(
                        _getNonVoicedriveScrollTarget(center_slide_id),
                    );
                    if (nonVoicedrivenScenePreview) {
                        scrollIntoView(nonVoicedrivenScenePreview, TRANSCRIPT_SCROLL_OPTIONS);
                    }
                }
            }
        }
    };
}

export function updateFocusedSceneInfoOnDelete(
    deletedSceneId: string,
    nextSceneId: string | undefined,
    nextSceneGroupId: string | undefined,
) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const focusedSceneId = getFocusedSceneId(getState());

        // check if the last deleted scene was the active scene.
        if (deletedSceneId === focusedSceneId) {
            if (nextSceneId && nextSceneGroupId) {
                dispatch(
                    setFocusedSceneInfo({
                        sceneId: nextSceneId,
                        sceneGroupId: nextSceneGroupId,
                        subsceneIndex: 0,
                    }),
                );
            } else {
                // we deleted the currently active scene, but
                // there's no next scene to go to. Reset the active scene.
                dispatch(
                    setFocusedSceneInfo({
                        sceneId: null,
                        sceneGroupId: null,
                        subsceneIndex: 0,
                    }),
                );
            }
        }
    };
}
